import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, ChevronRight, Filter, Check } from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/App";
import ListingCard from "@/components/ListingCard";
import { logger } from "@/utils/logger";

const SPECIES_OPTIONS = [
  { value: "equine", label: "Equine" },
  { value: "canine", label: "Canine" },
  { value: "feline", label: "Feline" },
  { value: "cattle", label: "Cattle" },
];

const BrowseFarmBreeding = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [filters, setFilters] = useState({
    species: searchParams.get("species") || "",
    in_season: searchParams.get("in_season") === "true",
    min_price: searchParams.get("min_price") || "",
    max_price: searchParams.get("max_price") || "",
  });

  useEffect(() => {
    const fetchFarmBreeding = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.species) params.append("species", filters.species);
        if (filters.in_season) params.append("in_season", "true");
        if (filters.min_price) params.append("min_price", filters.min_price);
        if (filters.max_price) params.append("max_price", filters.max_price);
        
        const response = await api.get(`/browse/farm-breeding?${params.toString()}`);
        setListings(response.data.listings || []);
        setTotal(response.data.total || 0);
      } catch (error) {
        logger.error("Failed to fetch farm breeding listings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFarmBreeding();
  }, [filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value.toString());
    } else {
      newParams.delete(key);
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setFilters({
      species: "",
      in_season: false,
      min_price: "",
      max_price: "",
    });
    setSearchParams(new URLSearchParams());
  };

  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="text-sm text-[#A1A1AA] hover:text-[#D4AF37] flex items-center gap-1 mb-4">
            ← Back to Breedling Home
          </Link>
          <div className="inline-block px-3 py-1 bg-[#22C55E]/20 text-[#22C55E] rounded-full text-xs font-medium mb-4">
            Farm-Grade
          </div>
          <h1 className="text-4xl md:text-5xl text-[#EDEDED] mb-4">Farm-Grade Breeding Stock</h1>
          <p className="text-[#A1A1AA]">Expand your gene pool with verified farm-grade studs and mares</p>
        </div>

        {/* Filter Toggle */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-[#A1A1AA]">{total} listing{total !== 1 ? 's' : ''} available</p>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-[#18181B] text-[#EDEDED] rounded-lg border border-[#3F3F46] hover:border-[#D4AF37] transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="glass-card p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg text-[#EDEDED]">Filter Results</h3>
              <button onClick={clearFilters} className="text-sm text-[#D4AF37] hover:underline">
                Clear All
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs text-[#A1A1AA] mb-2">Species</label>
                <select
                  value={filters.species}
                  onChange={(e) => handleFilterChange("species", e.target.value)}
                  className="w-full px-3 py-2 bg-[#18181B] text-[#EDEDED] rounded-lg border border-[#3F3F46] focus:border-[#D4AF37] outline-none"
                >
                  <option value="">All Species</option>
                  {SPECIES_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#A1A1AA] mb-2">Min Price</label>
                <input
                  type="number"
                  value={filters.min_price}
                  onChange={(e) => handleFilterChange("min_price", e.target.value)}
                  placeholder="$0"
                  className="w-full px-3 py-2 bg-[#18181B] text-[#EDEDED] rounded-lg border border-[#3F3F46] focus:border-[#D4AF37] outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-[#A1A1AA] mb-2">Max Price</label>
                <input
                  type="number"
                  value={filters.max_price}
                  onChange={(e) => handleFilterChange("max_price", e.target.value)}
                  placeholder="No max"
                  className="w-full px-3 py-2 bg-[#18181B] text-[#EDEDED] rounded-lg border border-[#3F3F46] focus:border-[#D4AF37] outline-none"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.in_season}
                    onChange={(e) => handleFilterChange("in_season", e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded border ${filters.in_season ? 'bg-[#22C55E] border-[#22C55E]' : 'bg-[#18181B] border-[#3F3F46]'} flex items-center justify-center`}>
                    {filters.in_season && <Check className="w-3 h-3 text-black" />}
                  </div>
                  <span className="text-sm text-[#EDEDED]">Available Now</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#22C55E]/10 flex items-center justify-center">
              <span className="text-4xl">🌾</span>
            </div>
            <p className="text-[#A1A1AA] text-lg mb-4">No farm-grade breeding stock available</p>
            <p className="text-[#71717A] text-sm mb-6">Be the first to list your farm-grade studs or mares</p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button 
                onClick={() => {
                  if (!user) {
                    navigate('/signin?returnUrl=/subscription-plans');
                  } else if (!user.roles?.includes('breeder')) {
                    navigate('/subscription-plans');
                  } else {
                    navigate('/dashboard/breeder/new-listing');
                  }
                }}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#22C55E] text-black font-medium rounded-lg hover:bg-[#16A34A] transition-colors"
              >
                Start Listing <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mt-8 pt-8 border-t border-[#27272A] max-w-md mx-auto">
              <p className="text-xs text-[#71717A] mb-4">Looking for other listings?</p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link to="/studs" className="text-sm text-[#D4AF37] hover:underline">Browse Studs</Link>
                <span className="text-[#3F3F46]">•</span>
                <Link to="/mares" className="text-sm text-[#D4AF37] hover:underline">Browse Mares</Link>
                <span className="text-[#3F3F46]">•</span>
                <Link to="/browse/equine" className="text-sm text-[#D4AF37] hover:underline">Browse Offspring</Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.map((listing) => (
              <ListingCard key={listing.listing_id} listing={listing} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrowseFarmBreeding;
